import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';

import * as yup from 'yup';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { FormFieldsContainer } from '../../../components/Form/FormComponents';
import { FormFieldAndLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { RendererType } from '../../../data-models/field-definition';
import { ISelectMeta } from '../../../data-models/field3.data-model';
import { portcosAtomJ } from '../../../services/state/PortCosStateJ';
import { createFormField } from '../../../view-models/form.view-model';
import { fundOptionsStateJ } from '../../PortfolioOverview/state/FundFilterStateJ';
import { useCashFlowsExport } from './useCashFlowsExport';

interface ExportCashFlowsDialogProps {
  open: boolean;
  onClose: () => void;
  preSelectedCompanyId?: number;
  preSelectedFundId?: number;
}

interface ExportFormData {
  fundIds: number[];
  companyIds: number[];
  asOfDate?: string;
}

const exportCashFlowsSchema = yup.object().shape({
  fundIds: yup.array().of(yup.number().required()).default([]),
  companyIds: yup.array().of(yup.number().required()).default([]),
  asOfDate: yup.string().optional(),
});

export function ExportCashFlowsDialog({
  open,
  onClose,
  preSelectedCompanyId,
  preSelectedFundId,
}: ExportCashFlowsDialogProps) {
  const { exportCashFlows, isExporting } = useCashFlowsExport();
  const [error, setError] = useState<string | null>(null);
  const portCos = useAtomValue(portcosAtomJ);
  const fundOptions = useAtomValue(fundOptionsStateJ);

  const methods = useForm<ExportFormData>({
    defaultValues: {
      fundIds: preSelectedFundId ? [preSelectedFundId] : [],
      companyIds: preSelectedCompanyId ? [preSelectedCompanyId] : [],
      asOfDate: undefined,
    },
    mode: 'all',
    resolver: yupResolver(exportCashFlowsSchema) as Resolver<ExportFormData>,
  });

  // Fund multi-select field
  const fundField = useMemo(
    () =>
      createFormField<ISelectMeta<number>>({
        key: 'fundIds',
        label: 'Funds',
        dataType: 'array',
        renderer: RendererType.multiSelect,
        rendererMeta: {
          values: fundOptions.map((f) => ({
            value: Number(f.id),
            displayName: f.value,
          })),
          multi: true,
        },
        required: false,
      }),
    [fundOptions]
  );

  // Company multi-select field - no filtering needed, backend handles it
  const companyField = useMemo(() => {
    return createFormField<ISelectMeta<number>>({
      key: 'companyIds',
      label: 'Companies',
      dataType: 'array',
      renderer: RendererType.multiSelect,
      rendererMeta: {
        values:
          portCos?.map((c) => ({
            value: c.id,
            displayName: c.name,
          })) || [],
        multi: true,
      },
      required: false,
    });
  }, [portCos]);

  // Date field
  const asOfDateField = createFormField({
    key: 'asOfDate',
    label: 'As Of Date',
    dataType: 'string',
    renderer: RendererType.date,
    required: false,
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      methods.reset({
        fundIds: preSelectedFundId ? [preSelectedFundId] : [],
        companyIds: preSelectedCompanyId ? [preSelectedCompanyId] : [],
        asOfDate: undefined,
      });
    }
  }, [open, preSelectedFundId, preSelectedCompanyId, methods]);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleExport = useCallback(
    async (data: ExportFormData) => {
      setError(null);

      try {
        await exportCashFlows({
          fundIds: data.fundIds,
          companyIds: data.companyIds,
          asOfDate: data.asOfDate,
        });
        handleClose(); // Close on success
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to export cash flows');
      }
    },
    [exportCashFlows, handleClose]
  );

  return (
    <BasicDialog open={open} onClose={handleClose} title='Export Cash Flows'>
      <FormProvider {...methods}>
        <FormFieldsContainer>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Select funds and companies to export cash flows. Leave empty to export all data.
          </Typography>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <FormFieldAndLabelFactory formField={fundField} />
            <FormFieldAndLabelFactory formField={companyField} />
            <FormFieldAndLabelFactory formField={asOfDateField} />
          </Stack>

          <Alert severity='info' sx={{ mt: 2 }}>
            <Typography variant='body2'>
              To obtain company-level cash flow information, go to the single company view
            </Typography>
          </Alert>
        </FormFieldsContainer>
        <StickyFormButtons
          onCancel={handleClose}
          onSubmit={methods.handleSubmit(handleExport)}
          submitLabel='Export Cash Flows'
          loading={isExporting}
        />
      </FormProvider>
    </BasicDialog>
  );
}
